import * as React from "react";
import { useMemo, useState } from "react";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import ArchiveCatalogTableEntry from "archive/components/ArchiveCatalogTableEntry";
import ArchiveCatalogExportModal from "archive/components/ArchiveCatalogExportModal";
import ArchiveActions from "archive/ArchiveActions";
import { PAGE_SIZES } from "archive/hooks/useArchivesQuery";
import { useArchivesQuery } from "archive/hooks";
import NumberUtils from "util/NumberUtils";
import {
  ConfirmDialog,
  IfPermitted,
  NoEntitiesExist,
  NoSearchResult,
  PaginatedList,
  SearchForm,
  Spinner
} from "components/common";
import { Table, DropdownButton, MenuItem, DeleteMenuItem } from "components/bootstrap";
import { deleteIndex } from "hooks/useIndices";
import usePaginationQueryParameter from "hooks/usePaginationQueryParameter";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import QueryHelper from "components/common/QueryHelper";
import OverlayTrigger from "components/common/OverlayTrigger";
import { StyledCatalogWrapperComponent } from "./StyledCatalogComponents";
const StyledDropDownbutton = styled(DropdownButton)`
  margin-left: 5px;
`;
const StyledPaginatedListContainer = styled.div`
  margin-top: 32px;
`;
const QUERY_EXAMPLE_DATE_RANGE = "from:>=2017-03-23 to:<=2017-04-01";
const QUERY_EXAMPLE_DATE_RANGE_QUOTED = 'from:">=2016-02-18 18:10:37.055" to:"<=2016-02-18 18:13:16.368"';
const QUERY_EXAMPLE_STREAM = "streams:errors";
const QUERY_EXAMPLE_STREAMS_AND = "streams:errors streams:warnings";
const QUERY_EXAMPLE_STREAMS_LIST = "streams:errors,warnings";
const QUERY_EXAMPLE_INDEX_AND_STREAM = "index:foo streams:errors";
const ArchiveCatalog = () => {
  var _a;
  const { t } = useTranslation("enterprise_archive");
  const fieldMap = {
    index: t("archive_catalog.field_map.index"),
    id: t("archive_catalog.field_map.id"),
    nodes: t("archive_catalog.field_map.nodes"),
    streams: t("archive_catalog.field_map.streams"),
    sources: t("archive_catalog.field_map.sources"),
    created: t("archive_catalog.field_map.created"),
    from: t("archive_catalog.field_map.from"),
    to: t("archive_catalog.field_map.to")
  };
  const queryExamples = /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "archive_catalog.query_examples.date_range" }), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, QUERY_EXAMPLE_DATE_RANGE), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, QUERY_EXAMPLE_DATE_RANGE_QUOTED)), /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "archive_catalog.query_examples.stream", components: { var: /* @__PURE__ */ React.createElement("var", null) } }), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, QUERY_EXAMPLE_STREAM)), /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "archive_catalog.query_examples.streams", components: { var: /* @__PURE__ */ React.createElement("var", null) } }), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, QUERY_EXAMPLE_STREAMS_AND), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, QUERY_EXAMPLE_STREAMS_LIST)), /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "archive_catalog.query_examples.index_and_stream", components: { var: /* @__PURE__ */ React.createElement("var", null) } }), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, QUERY_EXAMPLE_INDEX_AND_STREAM), /* @__PURE__ */ React.createElement("br", null)));
  const queryHelpComponent = /* @__PURE__ */ React.createElement(QueryHelper, { entityName: "archives", fieldMap, example: queryExamples, commonFields: [] });
  const { page, pageSize: per_page, resetPage } = usePaginationQueryParameter(PAGE_SIZES, (_a = PAGE_SIZES) == null ? void 0 : _a[0]);
  const [query, setQuery] = useState("");
  const [markedArchives, setMarkedArchives] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmRestore, setShowConfirmRestore] = useState(false);
  const [showAlreadyRestoredArchivesToRestore, setShowAlreadyRestoredArchivesToRestore] = useState(false);
  const [showArchivesMarkedToRestoreWithSameIndexName, setShowArchivesMarkedToRestoreWithSameIndexName] = useState(false);
  const sendTelemetry = useSendTelemetry("archive-catalog");
  const queryParams = useMemo(() => ({ page, per_page, query }), [page, per_page, query]);
  const {
    isLoading,
    data: { archives, archives_context: archivesContext, backends_context: backendsContext, total },
    refetch
  } = useArchivesQuery(queryParams);
  const onPaginationChange = () => {
    setMarkedArchives([]);
  };
  const onSearch = (newQuery, resetLoadingStateCb) => {
    resetPage();
    setQuery(newQuery);
    resetLoadingStateCb();
  };
  const onSearchReset = () => {
    resetPage();
    setQuery("");
  };
  const onMarkArchive = (event) => {
    const { checked, name } = event.target;
    if (checked) {
      setMarkedArchives([...markedArchives, name]);
    } else {
      setMarkedArchives(markedArchives.filter((archiveId) => archiveId !== name));
    }
  };
  const checkedMarkedArchive = (archiveId) => markedArchives.includes(archiveId);
  const getMarkedArchiveObjects = () => markedArchives.map((archiveId) => archives.find((archive) => archive.id === archiveId)).filter((archive) => archive !== void 0);
  const deleteArchive = (archive) => {
    ArchiveActions.deleteArchive(archive.backend_id, archive.archive_id).then(() => {
      const archiveContext = archivesContext[archive.backend_id][archive.archive_id];
      refetch();
      if (archiveContext.restored) {
        deleteIndex(archiveContext.restored_index_name);
      }
    });
  };
  const restoreArchives = (archivesToRestore) => {
    ArchiveActions.restoreArchives(archivesToRestore).then(() => {
      refetch();
    });
  };
  const deleteSelectedArchives = () => {
    setShowConfirmDelete(true);
  };
  const confirmDeleteSelectedArchives = () => {
    setShowConfirmDelete(false);
    getMarkedArchiveObjects().forEach((archive) => {
      deleteArchive(archive);
    });
    setMarkedArchives([]);
    sendTelemetry(TELEMETRY_EVENT_TYPE.ARCHIVE.DELETED, {
      app_pathname: "archive"
    });
  };
  const cancelDeleteSelectedArchives = () => {
    setShowConfirmDelete(false);
  };
  const getMarkedAlreadyRestoredArchives = () => getMarkedArchiveObjects().filter((archive) => {
    const archiveContext = archivesContext[archive.backend_id][archive.archive_id];
    return !!archiveContext.restored;
  });
  const getArchivesByIndexName = () => {
    const byIndexName = {};
    getMarkedArchiveObjects().forEach((archive) => {
      if (byIndexName[archive.index_name] === void 0) {
        byIndexName[archive.index_name] = [];
      }
      byIndexName[archive.index_name].push(archive);
    });
    return byIndexName;
  };
  const restoreSelectedArchives = () => {
    const multipleArchivesWithSameIndexNameMarked = Object.values(getArchivesByIndexName()).find(
      (archiveList) => archiveList.length > 1
    );
    if (getMarkedAlreadyRestoredArchives().length > 0) {
      setShowAlreadyRestoredArchivesToRestore(true);
    } else if (multipleArchivesWithSameIndexNameMarked) {
      setShowArchivesMarkedToRestoreWithSameIndexName(true);
    } else {
      setShowConfirmRestore(true);
    }
  };
  const confirmRestoreSelectedArchives = () => {
    setShowConfirmRestore(false);
    const archivesToRestore = getMarkedArchiveObjects();
    restoreArchives(archivesToRestore);
    setMarkedArchives([]);
    sendTelemetry(TELEMETRY_EVENT_TYPE.ARCHIVE.RESTORED, {
      app_pathname: "archive"
    });
  };
  const cancelRestoreSelectedArchives = () => {
    setShowConfirmRestore(false);
  };
  const hideAlreadyRestoredArchivesToRestoreDialog = () => {
    setShowAlreadyRestoredArchivesToRestore(false);
  };
  const hideArchivesMarkedToRestoreWithSameIndexNameDialog = () => {
    setShowArchivesMarkedToRestoreWithSameIndexName(false);
  };
  const sizeTextForArchive = (archive) => {
    const { index_size, segments } = archive;
    if (index_size > 0) {
      return t("archive_catalog.size_index", { size: NumberUtils.formatBytes(index_size) });
    }
    const segmentRawSize = segments.reduce((sum, segment) => sum + segment.raw_size, 0);
    if (segmentRawSize > 0) {
      return t("archive_catalog.size_uncompressed", { size: NumberUtils.formatBytes(segmentRawSize) });
    }
    return "";
  };
  const totalSizeTextForArchives = (archivesToRestore) => {
    const totalIndexSize = archivesToRestore.reduce((previousSize, archive) => archive.index_size + previousSize, 0);
    if (totalIndexSize > 0) {
      return t("archive_catalog.total_size_index", { size: NumberUtils.formatBytes(totalIndexSize) });
    }
    const segmentRawSize = archivesToRestore.reduce((previousSize, archive) => {
      const segmentSize = archive.segments.reduce((sum, segment) => sum + segment.raw_size, 0);
      return segmentSize + previousSize;
    }, 0);
    if (segmentRawSize > 0) {
      return t("archive_catalog.total_size_uncompressed", { size: NumberUtils.formatBytes(segmentRawSize) });
    }
    return "";
  };
  const restoreTextForArchive = (archive) => {
    const archiveContext = archivesContext[archive.backend_id][archive.archive_id];
    return archiveContext.restored ? t("archive_catalog.including_restored_index") : "";
  };
  const getArchiveRow = (archive, { withSize = false, withRestore = false } = {}) => /* @__PURE__ */ React.createElement("li", { key: archive.archive_id }, archive.archive_id, " (", archive.index_name, ")", withRestore && restoreTextForArchive(archive), withSize && sizeTextForArchive(archive));
  const archivesRows = archives.map((archive) => /* @__PURE__ */ React.createElement(
    ArchiveCatalogTableEntry,
    {
      key: `archive-entry-${archive.id}`,
      archive,
      archiveContext: archivesContext[archive.backend_id][archive.archive_id],
      backendContext: backendsContext[archive.backend_id],
      onMarkArchive,
      checkedMarkedArchive
    }
  ));
  const markedArchivesForBulkActions = getMarkedArchiveObjects();
  const dialogs = /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: showConfirmDelete,
      onConfirm: confirmDeleteSelectedArchives,
      onCancel: cancelDeleteSelectedArchives,
      title: t("archive_catalog.delete_archives_title")
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, t("archive_catalog.delete_archives_text", { number: markedArchivesForBulkActions.length })), /* @__PURE__ */ React.createElement("p", null, t("archive_catalog.archives_to_be_deleted")), /* @__PURE__ */ React.createElement("ul", { className: "pill-list" }, markedArchivesForBulkActions.map((archive) => getArchiveRow(archive, { withRestore: true }))), /* @__PURE__ */ React.createElement("p", null), /* @__PURE__ */ React.createElement("p", null, t("archive_catalog.delete_archives_confirm")))
  ), /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: showConfirmRestore,
      onConfirm: confirmRestoreSelectedArchives,
      onCancel: cancelRestoreSelectedArchives,
      title: t("archive_catalog.restore_archives_title")
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, t("archive_catalog.restore_archives_text", {
      number: markedArchivesForBulkActions.length,
      size: totalSizeTextForArchives(markedArchivesForBulkActions)
    })), /* @__PURE__ */ React.createElement("p", null, t("archive_catalog.archives_to_be_restored")), /* @__PURE__ */ React.createElement("ul", { className: "pill-list" }, markedArchivesForBulkActions.map((archive) => getArchiveRow(archive, { withSize: true }))), /* @__PURE__ */ React.createElement("p", null), /* @__PURE__ */ React.createElement("p", null, t("archive_catalog.restore_archives_confirm")))
  ), /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      hideCancelButton: true,
      btnConfirmText: t("archive_catalog.ok"),
      show: showAlreadyRestoredArchivesToRestore,
      onConfirm: hideAlreadyRestoredArchivesToRestoreDialog,
      title: t("archive_catalog.restore_archives_title")
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, t("archive_catalog.already_restored_text")), /* @__PURE__ */ React.createElement("p", null, t("archive_catalog.already_restored_list")), /* @__PURE__ */ React.createElement("ul", { className: "pill-list" }, getMarkedAlreadyRestoredArchives().map((archive) => getArchiveRow(archive))), /* @__PURE__ */ React.createElement("p", null), /* @__PURE__ */ React.createElement("p", null, t("archive_catalog.already_restored_hint")))
  ), /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      hideCancelButton: true,
      btnConfirmText: t("archive_catalog.ok"),
      show: showArchivesMarkedToRestoreWithSameIndexName,
      onConfirm: hideArchivesMarkedToRestoreWithSameIndexNameDialog,
      title: t("archive_catalog.restore_archives_title")
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, t("archive_catalog.same_index_name_text")), /* @__PURE__ */ React.createElement("p", null, t("archive_catalog.same_index_name_list")), /* @__PURE__ */ React.createElement("ul", { className: "pill-list" }, Object.values(getArchivesByIndexName()).flat().map((archive) => getArchiveRow(archive))))
  ));
  const toolltip = /* @__PURE__ */ React.createElement(React.Fragment, null, t("archive_catalog.mark_some_archives"));
  return isLoading ? /* @__PURE__ */ React.createElement(Spinner, { text: t("archive_catalog.loading") }) : /* @__PURE__ */ React.createElement(StyledCatalogWrapperComponent, null, dialogs, /* @__PURE__ */ React.createElement("h2", null, t("archive_catalog.title"), /* @__PURE__ */ React.createElement("span", null, "\xA0", /* @__PURE__ */ React.createElement("small", null, t("archive_catalog.total", { total, marked: markedArchivesForBulkActions.length })))), /* @__PURE__ */ React.createElement(StyledPaginatedListContainer, null, /* @__PURE__ */ React.createElement(PaginatedList, { totalItems: total, pageSizes: PAGE_SIZES, pageSize: per_page, onChange: onPaginationChange }, /* @__PURE__ */ React.createElement(
    SearchForm,
    {
      onSearch,
      onReset: onSearchReset,
      useLoadingState: true,
      queryHelpComponent
    },
    /* @__PURE__ */ React.createElement(StyledDropDownbutton, { keepMounted: true, id: "export-results-dropdown", title: t("archive_catalog.export_results") }, /* @__PURE__ */ React.createElement(ArchiveCatalogExportModal, { query: query || "" })),
    /* @__PURE__ */ React.createElement(IfPermitted, { permissions: ["archive:delete", "archive:restore"], anyPermissions: true }, /* @__PURE__ */ React.createElement(
      OverlayTrigger,
      {
        overlay: toolltip,
        trigger: ["hover", "focus"],
        disabled: markedArchives.length > 0,
        placement: "top"
      },
      /* @__PURE__ */ React.createElement(
        StyledDropDownbutton,
        {
          disabled: markedArchives.length === 0,
          id: "bulk-actions-dropdown",
          title: t("archive_catalog.bulk_actions")
        },
        /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "archive:delete" }, /* @__PURE__ */ React.createElement(DeleteMenuItem, { onClick: deleteSelectedArchives }), /* @__PURE__ */ React.createElement(MenuItem, { divider: true })),
        /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "archive:restore" }, /* @__PURE__ */ React.createElement(MenuItem, { onClick: restoreSelectedArchives }, t("archive_catalog.restore")))
      )
    ))
  ), !total && !query && /* @__PURE__ */ React.createElement(NoEntitiesExist, null, t("archive_catalog.no_entities")), !total && query && /* @__PURE__ */ React.createElement(NoSearchResult, null, t("archive_catalog.no_search_result")), !!total && /* @__PURE__ */ React.createElement(Table, { condensed: true }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: ["archive:delete", "archive:restore"], anyPermissions: true }, /* @__PURE__ */ React.createElement("th", null, t("archive_catalog.table.mark"))), /* @__PURE__ */ React.createElement("th", null, t("archive_catalog.table.index")), /* @__PURE__ */ React.createElement("th", null, t("archive_catalog.table.backend")), /* @__PURE__ */ React.createElement("th", null, t("archive_catalog.table.created")), /* @__PURE__ */ React.createElement("th", null, t("archive_catalog.table.range")), /* @__PURE__ */ React.createElement("th", null, t("archive_catalog.table.content")), /* @__PURE__ */ React.createElement("th", null, t("archive_catalog.table.streams")), /* @__PURE__ */ React.createElement("th", { className: "restored" }, t("archive_catalog.table.restored")))), archivesRows))));
};
export default ArchiveCatalog;
